import { runInInjectionContext } from '@angular/core';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ForeignKeyConfig } from '../field-definition';
import { resolveDisplayFromItem } from './fk-display-resolver.helper';

export type FkDisplayValueResolver = (item: any, displayField: string) => any;
export type FkRenderedValue =
  | string
  | HTMLElement
  | DocumentFragment
  | { html: HTMLElement | DocumentFragment }
  | { text: string };
export type FkRenderedValueResolver = (
  displayValue: any,
  context?: { row?: number; cell?: number; dataContext?: any; colDef?: any; cellNode?: HTMLElement }
) => FkRenderedValue;

export function createFkAsyncDisplayHandlers(
  fkConfig: ForeignKeyConfig,
  getDisplayValue: FkDisplayValueResolver = resolveDisplayFromItem,
  renderDisplayValue?: FkRenderedValueResolver
) {
  const cache = new Map<string, any>();
  const pendingCells = new Set<HTMLElement>();
  const pendingSingleValueLoads = new Set<string>();
  const queuedValueLoads = new Map<string, string | number>();
  const displayField = fkConfig.displayField;
  const keyField = fkConfig.keyField;
  const shouldRenderHtml = !!renderDisplayValue;
  const renderValue: FkRenderedValueResolver = renderDisplayValue ?? String;
  let batchScheduled = false;
  type PendingBatchItem = { key: string; rawValue: string | number };

  const cacheItem = (item: any): boolean => {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const key = String(item[keyField]);
    const displayValue = getDisplayValue(item, displayField);
    if (displayValue === undefined || displayValue === null) {
      return false;
    }

    cache.set(key, displayValue);
    return true;
  };

  const hydrateFromSharedCache = (value: string | number): boolean => {
    const sharedItem = fkConfig.getCachedItem?.(value);
    return cacheItem(sharedItem);
  };

  const formatter = (_row: number, _cell: number, value: any) => {
    if (value === null || value === undefined) {
      return '';
    }
    const valueStr = String(value);
    hydrateFromSharedCache(valueStr);
    if (cache.has(valueStr)) {
      return renderValue(cache.get(valueStr), { row: _row, cell: _cell });
    }
    return `ID: ${value}`;
  };

  const setCellValue = (cellNode: HTMLElement, renderedValue: FkRenderedValue) => {
    if (renderedValue instanceof HTMLElement || renderedValue instanceof DocumentFragment) {
      cellNode.replaceChildren(renderedValue.cloneNode(true));
      return;
    }

    if (typeof renderedValue === 'object' && renderedValue !== null) {
      if ('html' in renderedValue) {
        cellNode.replaceChildren(renderedValue.html.cloneNode(true));
        return;
      }

      if ('text' in renderedValue) {
        cellNode.textContent = renderedValue.text;
        return;
      }
    }

    if (shouldRenderHtml) {
      cellNode.innerHTML = typeof renderedValue === 'string' ? renderedValue : '';
      return;
    }

    cellNode.textContent = typeof renderedValue === 'string' ? renderedValue : '';
  };

  const updateCellIfCached = (cellNode: HTMLElement, valueStr: string): boolean => {
    if (!cellNode.isConnected) {
      return false;
    }
    const currentKey = cellNode.dataset.znFkKey;
    if (currentKey && currentKey !== valueStr) {
      return false;
    }
    hydrateFromSharedCache(valueStr);
    if (cache.has(valueStr)) {
      setCellValue(
        cellNode,
        renderValue(cache.get(valueStr), {
          dataContext: (cellNode as any).__znFkDataContext,
          colDef: (cellNode as any).__znFkColDef,
          cellNode,
        })
      );
      cellNode.dataset.znFkKey = valueStr;
      return true;
    }
    return false;
  };

  const flushPendingCells = () => {
    if (pendingCells.size === 0) return;
    pendingCells.forEach(cell => {
      const key = cell.dataset.znFkKey;
      if (!key || !cell.isConnected) {
        pendingCells.delete(cell);
        return;
      }
      if (updateCellIfCached(cell, key)) {
        pendingCells.delete(cell);
      }
    });
  };

  const finalizePendingBatch = (batch: PendingBatchItem[]) => {
    batch.forEach(({ key }) => pendingSingleValueLoads.delete(key));
    flushPendingCells();
  };

  const handleLoadedItems = (items: any[], batch: PendingBatchItem[]) => {
    items.forEach((item: any) => cacheItem(item));
    finalizePendingBatch(batch);
  };

  const createGetByIdRequest = (
    injector: any,
    getById: NonNullable<ForeignKeyConfig['getById']>,
    rawValue: string | number
  ) =>
    runInInjectionContext(injector, () =>
      getById(rawValue, { skipHandleError: true })
    ).pipe(map((item: any) => item ?? null));

  const loadBatchById = (
    injector: any,
    getById: NonNullable<ForeignKeyConfig['getById']>,
    batch: PendingBatchItem[]
  ) => {
    forkJoin(batch.map(({ rawValue }) => createGetByIdRequest(injector, getById, rawValue))).subscribe({
      next: (items: any[]) => handleLoadedItems(items, batch),
      error: () => finalizePendingBatch(batch),
    });
  };

  const processQueuedValueLoads = (injector: any) => {
    const batch = Array.from(queuedValueLoads.entries()).map(([key, rawValue]) => ({ key, rawValue }));
    queuedValueLoads.clear();

    if (!batch.length) {
      return;
    }

    const getById = fkConfig.getById;
    if (!getById) {
      finalizePendingBatch(batch);
      return;
    }

    loadBatchById(injector, getById, batch);
  };

  const requestSingleValue = (
    injector: any,
    value: string | number,
    valueStr: string
  ) => {
    hydrateFromSharedCache(valueStr);
    if (pendingSingleValueLoads.has(valueStr) || cache.has(valueStr)) {
      return;
    }

    if (fkConfig.getByIds) {
      return;
    }

    pendingSingleValueLoads.add(valueStr);
    queuedValueLoads.set(valueStr, value);

    if (batchScheduled) {
      return;
    }

    batchScheduled = true;
    setTimeout(() => {
      batchScheduled = false;
      processQueuedValueLoads(injector);
    }, 0);
  };

  const createAsyncPostRender = (injector: any) => {
    return (cellNode: HTMLElement, _row: number, dataContext: any, colDef: any) => {
      if (cellNode.querySelector('.zn-select-editor-host')) {
        return;
      }
      (cellNode as any).__znFkDataContext = dataContext;
      (cellNode as any).__znFkColDef = colDef;
      const value = dataContext[colDef.field!];
      if (value === null || value === undefined) {
        cellNode.textContent = '';
        delete cellNode.dataset.znFkKey;
        pendingCells.delete(cellNode);
        return;
      }

      const valueStr = String(value);
      cellNode.dataset.znFkKey = valueStr;
      if (!updateCellIfCached(cellNode, valueStr)) {
        pendingCells.add(cellNode);
        if (typeof value === 'number' || typeof value === 'string') {
          requestSingleValue(injector, value, valueStr);
        }
      }
    };
  };

  return { formatter, createAsyncPostRender };
}
